import { Skeleton } from '@/components/Skeleton';
import { StagedPlaceholder } from '@/components/StagedPlaceholder';
import { Undo2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/hooks/useStores';
import { phoneAction, ScopeLineChevron } from './phoneChromeCore';
import { postShellMessage } from './shellBridge';
import {
  emptyScopeLineOf,
  lineScopeOf,
  scopeChoosable,
  scopeLineOf,
  appScopeTitleIn,
  scopeLineText,
  scopeLineWorkAreaOf,
  workAreaOfPath,
  workAreaSwitcherMessage,
} from './appScope';
import { waitingElsewhereLabel, waitingElsewhereOf, waitingEverywhere } from './waitingCounts';
import type { IAppScopeNotice } from './useAppScope';
import { ConfirmationToast } from '@/components/ConfirmationToast';
import type { ProjectStore } from '@/stores/ProjectStore';
import type { IProject } from '@featit/shared';
import { useMemo, type ReactNode } from 'react';
import { useT } from '@/i18n/LocaleProvider';
import { useWorkAreaNavigationPreferences } from '@/features/work-areas/useWorkAreaNavigationPreferences';
import { sharedSwitcherOrder } from '@/features/settings/usePhonePreferences';
import {
  groupWorkAreasByNamespace,
  type IWorkAreaNamespaceGroup,
} from '@/components/workAreaNamespaceGroups';

/**
 * F-0530 (AC-19.2, AC-19.3; Jay, 2026-10-06): the scope line — the one small line under the page's
 * title in the iPhone app, where the scope is shown and changed. The work area's display name, in
 * words with no badge (AC-19.14, I-1348) and with its namespace only where the name alone is
 * ambiguous (`scopeLineQualifier`; never a namespace handle, never an "All work areas" link), and a
 * quiet ▾ when there is more than one to choose from. A tap asks the shell for its native sheet; with one work area it is the
 * name alone and a tap does nothing.
 *
 * ⛔ The one place the phone draws the scope (`appScopeTitle.guard.test.ts`): `Layout` draws it, and
 * a page's own scope line (`NamespaceScopeBanner`) gives way to it in the app.
 */
export const AppScopeLine = observer(function AppScopeLine() {
  const { projectStore, namespaceStore } = useStores();
  const location = useLocation();
  const ordered = useOrderedWorkAreas();
  const t = useT();
  const scope = lineScopeOf(location.pathname, projectStore.appScope, projectStore.scopeWorkAreaId);
  // I-2362: All work areas in the reader's language; a work area's or namespace's name as it is.
  const line = appScopeTitleIn(
    t,
    scopeLineOf(
      location.pathname,
      scope,
      projectStore.projects,
      namespaceStore?.tree ?? null,
      projectStore.selectedWorkAreaName,
      projectStore.sessionsOutsideWorkAreas,
    ),
  );
  // In the top row's slot: as wide as its name needs, and truncating only when the row is full.
  // I-1327: with the row's own 8 and the content's 4, the name starts at the phone's margin,
  // `--phone-list-text`, the column a tab root's list writes in (no badge since AC-19.14).
  // I-1296 reopened (IU-14): the line is a bar's, so its type follows the text size to 15/20 and
  // stops there, as the narrow header's scope does (CFC-f5c). In `rem` it was 41/62 at 3.12, and the
  // line stood 65 px tall in its 44 px row, from 10 px above the screen to 11 px below the row.
  const box =
    'flex min-h-[32px] min-w-0 items-center gap-2 px-1 text-[length:min(0.8125rem,15px)] leading-[min(1.25rem,20px)]';
  const lead = 'inline-flex min-w-0 items-center gap-[var(--phone-scope-gap,8px)] px-1';
  if (!line) {
    /*
      RD-20 (I-1077): the placeholder says a read is under way, so it is drawn only while one is.
      Drawn whenever there was nothing to name, it pulsed for ever for a person with no work area,
      and the launch's mark, which waits for every placeholder to go (`whenLaunchContentSettles`),
      held each tab's first screen until its 10 s give-up.
    */
    const empty = emptyScopeLineOf(
      location.pathname,
      {
        workAreasAnswered: projectStore.hasWorkAreaAnswer,
        workAreasFailed: projectStore.failure !== null,
        namespacesAnswered: (namespaceStore?.tree ?? null) !== null,
        namespacesFailed: (namespaceStore?.failure ?? null) !== null,
      },
      scopeChoosable(projectStore.projects.length, projectStore.sessionsOutsideWorkAreas),
    );
    if (empty === 'loading') {
      // Nothing to name yet: a placeholder the width of a short name, never a guess.
      return (
        <div data-app-scope-line="" className={box}>
          {/* F-0604 WAIT-20 to WAIT-22: the name's bar, the study as one line at 2 s and Try again at the
              deadline; the work areas are the app's own read, so Try again reads the screen again. */}
          <StagedPlaceholder aria-label={t('navigation.scopeLine.loading')}>
            <Skeleton className="h-[14px] w-28" />
          </StagedPlaceholder>
        </div>
      );
    }
    // RD-21: answered, several to choose from and none named here — the choice, never a guess.
    if (empty === 'choose') {
      return (
        <div data-app-scope-line="" className={box}>
          <button
            type="button"
            {...phoneAction('switch-scope')}
            aria-haspopup="dialog"
            onClick={() => openWorkAreaSwitcher(projectStore, location.pathname, ordered)}
            className={`${lead} min-h-touch rounded-lg outline-none active:bg-black/5 focus-visible:ring-2 focus-visible:ring-focus dark:active:bg-white/10`}
          >
            <span className="min-w-0 truncate font-medium text-gray-700 dark:text-gray-200">
              {t('chromeTitles.chooseWorkArea')}
            </span>
            <ScopeLineChevron />
          </button>
        </div>
      );
    }
    /*
      No work area, or the list failed (the page says so, with Try again): nothing to name, and
      RD-27 (I-1482): nothing drawn. Its empty box kept the top row 44 px tall, a blank band over
      the page; with the scope slot empty the row's content collapses (`index.css`).
    */
    return null;
  }
  /*
    AC-19.8: the badge counts this work area, so what waits in the others is said here — nothing is
    hidden. After the name, quieter than it, and the first thing to give way when the row is full.
  */
  const elsewhere = waitingElsewhereLabel(
    waitingElsewhereOf(
      waitingEverywhere.decisions ?? [],
      scopeLineWorkAreaOf(location.pathname, scope, projectStore.projects),
    ),
  );
  const note = elsewhere ? (
    <span
      data-app-scope-elsewhere=""
      className="min-w-0 shrink truncate text-[length:min(0.75rem,14px)] text-gray-500 dark:text-gray-400"
    >
      · {elsewhere}
    </span>
  ) : null;
  /*
    AC-19.14: where a namespace is named, it is what gives way when the row is full ("Evoye Res… /
    code"; its `shrink` outweighs the name's by four orders): the work area's own name is the
    line's point, and it is cut only once the namespace has gone.
  */
  const content = (
    <span
      data-app-scope-name=""
      className="flex min-w-0 font-medium text-gray-700 dark:text-gray-200"
    >
      {line.qualifier ? (
        <>
          <span className="min-w-0 shrink-[10000] truncate">{line.qualifier}</span>
          <span className="shrink-0 whitespace-pre"> / </span>
        </>
      ) : null}
      <span className="min-w-0 truncate">{line.name}</span>
    </span>
  );
  if (!line.choosable) {
    return (
      <p data-app-scope-line="" className={box}>
        {/* The button's own box, so the name starts where it does when there is a choice. */}
        <span className={lead}>{content}</span>
        {note}
      </p>
    );
  }
  return (
    <div data-app-scope-line="" className={box}>
      <button
        type="button"
        {...phoneAction('switch-scope')}
        aria-label={t('navigation.scopeLine.button', { label: scopeLineText(line) })}
        aria-haspopup="dialog"
        onClick={() => openWorkAreaSwitcher(projectStore, location.pathname, ordered)}
        // 44 pt tall to the touch, drawn as one quiet line.
        className={`${lead} min-h-touch rounded-lg outline-none active:bg-black/5 focus-visible:ring-2 focus-visible:ring-focus dark:active:bg-white/10`}
      >
        {content}
        <ScopeLineChevron />
      </button>
      {note}
    </div>
  );
});

/**
 * AC-19.3: the request the scope line and the drawer's WORK AREA row both send, so the shell shows one
 * sheet from either, in the order the person's setting names (AC-19.18).
 */
export function openWorkAreaSwitcher(
  projectStore: ProjectStore,
  pathname: string,
  ordered: readonly IProject[],
): boolean {
  // AC-19.5: one work area, nothing to choose; the line draws no ▾ to tap. I-1472: unless
  // something lies outside it, where All work areas is the way to reach it.
  const outside = projectStore.sessionsOutsideWorkAreas;
  if (!scopeChoosable(projectStore.projects.length, outside)) return false;
  const shown = workAreaOfPath(pathname, projectStore.projects)?.id ?? projectStore.scopeWorkAreaId;
  return postShellMessage(
    workAreaSwitcherMessage(
      projectStore.appScope,
      shown,
      ordered,
      projectStore.recentWorkAreaIds,
      sharedSwitcherOrder(),
      outside,
    ),
  );
}

/**
 * AC-19.3: the work areas in the person's own order — the edit view's, the sidebar's — which the
 * sheet's full list keeps. The same grouping the picker draws (`groupWorkAreasByNamespace`).
 */
export function orderedWorkAreas(groups: readonly IWorkAreaNamespaceGroup[]): IProject[] {
  return groups.flatMap((group) => group.rows.map((row) => row.project));
}

export function useOrderedWorkAreas(): IProject[] {
  const { projectStore, namespaceStore } = useStores();
  const { preferences } = useWorkAreaNavigationPreferences();
  const projects = projectStore.projects;
  const tree = namespaceStore?.tree ?? null;
  return useMemo(
    () =>
      orderedWorkAreas(
        groupWorkAreasByNamespace(
          projects,
          tree,
          preferences?.workAreaOrder,
          preferences?.namespaceOrder,
        ),
      ),
    [projects, tree, preferences?.workAreaOrder, preferences?.namespaceOrder],
  );
}

/**
 * AC-19.6: "Now in X · Undo", until Undo, a dismissal or six seconds. Drawn the one way every
 * confirmation is (I-1190), so on a phone it stands on the one notice offset above the tab bar,
 * mobile web's own bar and its ＋ (I-1152) — never the shell inset alone, 0 in a phone browser.
 */
export function AppScopeNotice({ notice }: { notice: IAppScopeNotice }) {
  const t = useT();
  if (notice.kind === 'gone') {
    // I-1876 (MWI-27): why the window is not where the person left it, once; nothing to undo.
    return (
      <ConfirmationToast
        testId="app-scope-notice"
        message={
          notice.name
            ? t.rich('navigation.workAreaGone', {
                name: notice.name,
                place: (chunks: ReactNode) => <span className="font-medium">{chunks}</span>,
              })
            : t('navigation.workAreaGoneUnnamed')
        }
      />
    );
  }
  if (notice.kind === 'unavailable') {
    // I-1213: why the app is not where the person left it, once; nothing to undo.
    return (
      <ConfirmationToast
        testId="app-scope-notice"
        message={
          notice.name
            ? t.rich('navigation.scopeLine.unavailable', {
                name: notice.name,
                place: (chunks: ReactNode) => <span className="font-medium">{chunks}</span>,
              })
            : t('navigation.scopeLine.unavailableUnnamed')
        }
      />
    );
  }
  const undo = notice.undo;
  return (
    <ConfirmationToast
      testId="app-scope-notice"
      message={t.rich('navigation.scopeLine.nowIn', {
        name: notice.name ?? '',
        place: (chunks: ReactNode) => <span className="font-medium">{chunks}</span>,
      })}
      action={
        undo
          ? {
              label: t('navigation.scopeLine.undo'),
              icon: Undo2,
              testId: 'app-scope-notice-undo',
              onClick: undo,
            }
          : undefined
      }
    />
  );
}
